import * as React from 'react';
import classnames from 'classnames';
import './Base.css';

export type TypeBorderRadius = 'x1' | 'x2' | 'x4' | 'rounded';

export type TypeColors =
  'black' | 'white' |
  'gray-1' | 'gray-2' | 'gray-3' | 'gray-4' | 'gray-5' | 'gray-6' | 'gray-7' | 'gray-8' |
  'aqua-1' | 'aqua-2' | 'aqua-3' | 'aqua-4' | 'aqua-5' |
  'grape-1' | 'grape-2' | 'grape-3' | 'grape-4' | 'grape-5' |
  'eggplant' |
  'red-1' | 'red-2' | 'red-3' | 'red-4' | 'red-5' |
  'orange-1' | 'orange-2' | 'orange-3' | 'orange-4' | 'orange-5' |
  'yellow-1' | 'yellow-2' | 'yellow-3' | 'yellow-4' | 'yellow-5' |
  'green-1' | 'green-2' | 'green-3' | 'green-4' | 'green-5' |
  'blue-1' | 'blue-2' | 'blue-3' | 'blue-4' | 'blue-5' |
  'darkblue-1' | 'darkblue-2' | 'darkblue-3' | 'darkblue-4' | 'darkblue-5' |
  'violet-1' | 'violet-2' | 'violet-3' | 'violet-4' | 'violet-5';

export type TypeSizes = 'x0' | 'x1' | 'x2' | 'x3' | 'x4' | 'x5' | 'x6' | 'x8' | 'x10' | 'x12' | 'x16';

export interface Props {
  /**
   * Sets the type of component to be rendered. Can be a
   * string for an HTML tag or a React component.
   * */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component?: string | React.ComponentType<any>;
  /** Absolute positioning, useful for quick centering in a relative surrounding. */
  absolute?: boolean | 'center';
  /** Sets the background color of any of the valid swatch colors. */
  backgroundColor?: TypeColors;
  /** Sets the border color of any of the valid swatch colors. */
  borderColor?: TypeColors;
  /** Sets the border radius to a multiple value. E.g. 'x1'. */
  borderRadius?: TypeBorderRadius;
  /** Sets the border radius to a multiple value. E.g. 'x1'. */
  borderWidth?: 'x1' | 'x2' | 'x3';
  /** Standard class name property. */
  className?: string;
  /** Sets the border radius to a multiple value. E.g. x1 */
  clickable?: boolean;
  /** Sets relative positioning */
  container?: boolean;
  /** Sets an elevated style with box-shadow, with a multiple value. E.g. x1 */
  elevation?: 'x1' | 'x2' | 'x3';
  /** Sets inline height styling  */
  height?: number | string;
  /** Similar to other libraries, an alias to pass a ref to the final React element. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  innerRef?: React.Ref<any>;
  /** Sets the margin to a multiple value. E.g. 'x1'. */
  margin?: TypeSizes;
  /** Sets inline max-height styling  */
  maxHeight?: number | string;
  /** Sets inline max-width styling  */
  maxWidth?: number | string;
  /** Sets inline min-height styling  */
  minHeight?: number | string;
  /** Sets inline min-width styling  */
  minWidth?: number | string;
  /** Sets the vertical and horizontal padding to a multiple value. E.g. 'x1'. */
  padding?: TypeSizes;
  /** Sets the horizontal padding to a multiple value. E.g. 'x1'. */
  paddingHorizontal?: TypeSizes;
  /** Sets the vertical padding to a multiple value. E.g. 'x1'. */
  paddingVertical?: TypeSizes;
  /** Sets the overflow */
  overflow?: 'hidden' | 'scroll' | 'auto';
  /** Standard style property. */
  style?: React.CSSProperties | { [key: string]: number | string };
  /** @ignore */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ref?: React.Ref<any>;
  /** Sets the font color of any of the valid swatch colors. */
  textColor?: TypeColors;
  /** Sets inline width styling  */
  width?: number | string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

/**
 * The Base component is the end of the line for all components and
 * provides general and utility like styling.
 * */
const Base: React.FunctionComponent<Props> = (props: Props) => {
  const {
    Component,
    absolute,
    backgroundColor,
    borderColor,
    borderWidth = borderColor ? 'x1' : undefined,
    borderRadius,
    children,
    className,
    clickable,
    container,
    elevation,
    height,
    margin,
    maxHeight,
    maxWidth,
    minHeight,
    minWidth,
    padding,
    paddingHorizontal = padding,
    paddingVertical = padding,
    overflow,
    style,
    textColor,
    width,
    ...rest
  } = props;

  const FinalComponent = Component || 'div';
  const classes = classnames('Base', {
    'Base--absolute': absolute === true,
    [`Base--absolute-${absolute}`]: typeof absolute === 'string',
    [`Base--background-color-${backgroundColor}`]: backgroundColor,
    [`Base--border-radius-${borderRadius}`]: borderRadius,
    [`Base--border-color-${borderColor}`]: borderColor,
    [`Base--border-width-${borderWidth}`]: borderWidth,
    'Base--clickable': clickable,
    'Base--container': container,
    [`Base--elevation-${elevation}`]: elevation,
    [`Base--margin-${margin}`]: margin,
    'Base--max-width': maxWidth,
    [`Base--overflow-${overflow}`]: overflow,
    [`Base--padding-horizontal-${paddingHorizontal}`]: paddingHorizontal,
    [`Base--padding-vertical-${paddingVertical}`]: paddingVertical,
    [`Base--text-color-${textColor}`]: textColor,
  }, className);

  // Work around for findDOMNode area causing constant renders. Forwards
  // refs for component compositions.
  if (typeof FinalComponent === 'string') {
    rest.ref = props.innerRef;
    delete rest.innerRef;
  }

  return (
    <FinalComponent { ...rest }
        className={ classes }
        style={ { ...style, height, maxHeight, maxWidth, minHeight, minWidth, width } }>
      { children }
    </FinalComponent>
  );
};

export default Base;
